// Shared multi-week clipping helpers.
//
// In a consolidated multi-week run, every time-related field on an
// InputRow carries ABSOLUTE consolidated-horizon hours (0 for week-1
// start, 168 for week-2 start, etc.). The per-week views render against
// a fixed 168-hour grid, so each row's times must be shifted by
// `(selectedWeek - earliestConsolidatedWeek) * 168` and clipped to
// `[0, WEEK_HOURS]` before they hit the slot generators.
//
// The legacy single-CIP fields (`kitchen_clean_start_time` etc.) and the
// scalar changeover fields (`kitchen_changeover_start_time` etc.) are
// plain numbers; the per-event JSON fields (`additional_kitchen_cips`,
// `additional_line_cips`, `additional_kitchen_changeovers`,
// `additional_line_changeovers`) are strings containing
// `[{start, end, reason?}, ...]` arrays — those need their inner
// `start`/`end` clipped too, with off-window events dropped entirely
// rather than clamped to 0 (which would draw spurious slots at hour 0).

import { InputRow } from './SchedulerView.types'

export const WEEK_HOURS = 168

// Kept as (start, end) PAIRS rather than a flat list so clipping can tell
// which edge of an interval it truncated — that is what `WeekClipMarks`
// below records, and what lets a renderer mark a bar as continuing outside
// the week on screen.
//
// The scalar changeover pair is easy to forget: it is the one activity
// `scheduler_api`'s `row_activity_span` counts when deciding which week
// slices a row lands in that is NOT production or cleaning, so a row can be
// bucketed into this week on the strength of its changeover alone. Left
// absolute it renders off a 0-168 grid and vanishes from both weeks.
const TIME_FIELD_PAIRS = [
  ['kitchen_start_time', 'kitchen_end_time'],
  ['line_start_time', 'line_end_time'],
  ['kitchen_clean_start_time', 'kitchen_clean_end_time'],
  ['kitchen_preclean_start_time', 'kitchen_preclean_end_time'],
  ['line_clean_start_time', 'line_clean_end_time'],
  ['line_preclean_start_time', 'line_preclean_end_time'],
  ['kitchen_changeover_start_time', 'kitchen_changeover_end_time'],
  ['line_changeover_start_time', 'line_changeover_end_time'],
] as const

const ADDITIONAL_EVENT_FIELDS = [
  'additional_kitchen_cips',
  'additional_line_cips',
  'additional_kitchen_changeovers',
  'additional_line_changeovers',
] as const

type AdditionalEvent = {
  start: number
  end: number
  [k: string]: unknown
}

/**
 * Which edges of which intervals `clipRowToWeek` had to truncate, keyed by
 * the interval's START field name (so a renderer looks it up with the same
 * field it reads its times from).
 *
 * `start` means the activity began in an earlier week — it is carry-over,
 * and its bar has been pulled forward to hour 0. `end` means it continues
 * into a later week and has been cut off at hour 168. Neither is visible in
 * the clipped numbers themselves, which is the whole reason this exists.
 */
export type WeekClipMarks = Record<string, { start?: true; end?: true }>

/** Property the marks are stashed under. Prefixed so it can never collide
 *  with a Delta column name. */
export const WEEK_CLIP_MARKS = '_weekClipMarks'

/** Marks left on `row` by `clipRowToWeek`, or `{}` for an unclipped row. */
export const clipMarksOf = (row: InputRow): WeekClipMarks =>
  ((row as unknown as Record<string, unknown>)[WEEK_CLIP_MARKS] as
    | WeekClipMarks
    | undefined) ?? {}

/**
 * The offset to subtract from this iteration's rows to land them in `week`'s
 * slice, or `null` when the rows are ordinary per-week times that must not
 * be touched at all.
 *
 * `null` rather than `0` deliberately: those two cases are NOT the same, and
 * conflating them is what left the earliest consolidated week unclipped for
 * so long. Week 1 of a consolidated run has offset 0 but still has to be
 * clipped at hour 168, or a run spanning into week 2 gets counted in full by
 * both weeks. A single-week schedule legitimately runs past 168 and must be
 * left alone.
 */
export const weekBaseHourFor = (
  consolidatedWeeks: number[] | undefined,
  week: number
): number | null =>
  consolidatedWeeks && consolidatedWeeks.length >= 2
    ? (week - consolidatedWeeks[0]) * WEEK_HOURS
    : null

/**
 * The weeks a consolidated run's export should cover: its own weeks, then
 * each following week (up to 8) whose rows serve its demand. scheduler_api
 * (0.4.13+) writes production that runs past the last consolidated week into
 * the week it falls in rather than hiding it under the last one; exporting
 * only the consolidated weeks would leave those runs out of the file. Stops
 * at the first following week with no such rows. `rowsOf` is the caller's
 * own cached read, so the week loop that follows re-reads from cache.
 */
export const weeksWithOverflow = async (
  consolidatedWeeks: number[],
  rowsOf: (week: number) => Promise<InputRow[]>
): Promise<number[]> => {
  const weeks = [...consolidatedWeeks].sort((a, b) => a - b)
  const own = new Set(weeks)
  for (let w = weeks[weeks.length - 1] + 1; w <= 8; w++) {
    const rows = await rowsOf(w)
    const serves = rows.some(
      (r) =>
        Number(r.weekNumber) === w &&
        r.demandWeek != null &&
        own.has(Number(r.demandWeek))
    )
    if (!serves) break
    weeks.push(w)
  }
  return weeks
}

// Parse, filter, shift and re-serialise the JSON-event array. Events that
// don't overlap the week are dropped (not clamped to 0) so the grid
// doesn't show a phantom slot at hour 0. Malformed values pass through
// unchanged so we never silently lose information.
const clipAdditionalEvents = (raw: unknown, weekBaseHour: number): unknown => {
  if (raw === null || raw === undefined || raw === '') return raw
  let arr: unknown
  try {
    arr = typeof raw === 'string' ? JSON.parse(raw) : raw
  } catch {
    return raw
  }
  if (!Array.isArray(arr)) return raw

  const out: AdditionalEvent[] = []
  for (const ev of arr) {
    if (!ev || typeof ev !== 'object') continue
    const e = ev as AdditionalEvent
    if (typeof e.start !== 'number' || typeof e.end !== 'number') continue
    const relS = e.start - weekBaseHour
    const relE = e.end - weekBaseHour
    if (relE <= 0 || relS >= WEEK_HOURS) continue
    out.push({
      ...e,
      start: Math.max(0, Math.min(WEEK_HOURS, relS)),
      end: Math.max(0, Math.min(WEEK_HOURS, relE)),
    })
  }
  return JSON.stringify(out)
}

// Subtract weekBaseHour from every numeric time field on the row, clip
// to [0, WEEK_HOURS], and clip each event inside the `additional_*`
// JSON arrays the same way. A run at absolute hours 140–210 in slice 1
// (weekBaseHour=168) becomes 0–42 here; a kitchen CIP at absolute 274
// inside `additional_kitchen_cips` becomes 106.
//
// Clips at BOTH edges even when weekBaseHour is 0. That zero is week 1 of a
// consolidated run, not "nothing to do": the same 140–210 run has to read
// 140–168 there, or week 1 counts all 70 of its hours and week 2 counts 42
// of them again. Call this only for a genuinely consolidated iteration —
// `weekBaseHourFor` returns `null`, not 0, for every other case, and
// `rebaseRowsToWeek` is the guarded entry point.
export const clipRowToWeek = (
  row: InputRow,
  weekBaseHour: number
): InputRow => {
  const rec = row as unknown as Record<string, unknown>
  const out: Record<string, unknown> = { ...rec }
  const marks: WeekClipMarks = {}
  for (const [startField, endField] of TIME_FIELD_PAIRS) {
    const s = rec[startField]
    if (typeof s === 'number') {
      const rel = s - weekBaseHour
      out[startField] = Math.max(0, Math.min(WEEK_HOURS, rel))
      if (rel < 0) marks[startField] = { ...marks[startField], start: true }
    }
    const e = rec[endField]
    if (typeof e === 'number') {
      const rel = e - weekBaseHour
      out[endField] = Math.max(0, Math.min(WEEK_HOURS, rel))
      if (rel > WEEK_HOURS) {
        marks[startField] = { ...marks[startField], end: true }
      }
    }
  }
  for (const f of ADDITIONAL_EVENT_FIELDS) {
    if (f in rec) {
      out[f] = clipAdditionalEvents(rec[f], weekBaseHour)
    }
  }
  if (Object.keys(marks).length > 0) out[WEEK_CLIP_MARKS] = marks
  return out as unknown as InputRow
}

/**
 * Rebase a whole row set into `week`'s slice — the entry point every view
 * should use. Pass the result of `weekBaseHourFor`; `null` returns `rows`
 * untouched, so a single-week schedule is never clipped.
 *
 * The overlap filter drops a row that `scheduler_api` bucketed into this
 * week only because of a different resource (see `rowOverlapsWeek`).
 */
export const rebaseRowsToWeek = (
  rows: InputRow[],
  weekBaseHour: number | null
): InputRow[] =>
  weekBaseHour === null
    ? rows
    : rows
        .filter((r) => rowOverlapsWeek(r, weekBaseHour))
        .map((r) => clipRowToWeek(r, weekBaseHour))

// True iff any kitchen-or-line activity interval on the row overlaps
// this week's [0, WEEK_HOURS] slice in absolute time. Used to drop a
// row that was bucketed into this week only because of a different
// resource — e.g. a kitchen-only activity ending in week 1 shouldn't
// appear in week 2's line tab.
//
// Preclean/postclean intervals are checked too: a CIP can fall in a
// different week than the row's primary production (e.g. a line
// preclean at hour 163 belonging to a row whose production runs at
// 176-197 in week 2). Without including the CIP intervals here the
// row gets filtered out of week 1, and its CIP — which clipRowToWeek
// would happily preserve — disappears from cleaning/line/combined
// views entirely. `additional_*` event arrays are inspected the
// same way so out-of-band CIPs/changeovers also surface.
export const rowOverlapsWeek = (
  row: InputRow,
  weekBaseHour: number
): boolean => {
  const rec = row as unknown as Record<string, unknown>
  const candidates: Array<[unknown, unknown]> = [
    [rec.kitchen_start_time, rec.kitchen_end_time],
    [rec.line_start_time, rec.line_end_time],
    [rec.kitchen_preclean_start_time, rec.kitchen_preclean_end_time],
    [rec.kitchen_clean_start_time, rec.kitchen_clean_end_time],
    [rec.line_preclean_start_time, rec.line_preclean_end_time],
    [rec.line_clean_start_time, rec.line_clean_end_time],
    // Matches `row_activity_span` server-side: a changeover is enough to put
    // a row in this slice, so it has to be enough to keep it here too.
    [rec.kitchen_changeover_start_time, rec.kitchen_changeover_end_time],
    [rec.line_changeover_start_time, rec.line_changeover_end_time],
  ]
  for (const [s, e] of candidates) {
    if (typeof s === 'number' && typeof e === 'number') {
      const relS = s - weekBaseHour
      const relE = e - weekBaseHour
      if (relE > 0 && relS < WEEK_HOURS) return true
    }
  }
  for (const f of ADDITIONAL_EVENT_FIELDS) {
    const raw = rec[f]
    if (raw === null || raw === undefined || raw === '') continue
    let arr: unknown
    try {
      arr = typeof raw === 'string' ? JSON.parse(raw) : raw
    } catch {
      continue
    }
    if (!Array.isArray(arr)) continue
    for (const ev of arr) {
      if (!ev || typeof ev !== 'object') continue
      const e = ev as { start?: unknown; end?: unknown }
      if (typeof e.start === 'number' && typeof e.end === 'number') {
        const relS = e.start - weekBaseHour
        const relE = e.end - weekBaseHour
        if (relE > 0 && relS < WEEK_HOURS) return true
      }
    }
  }
  return false
}
